/**
 * messagingNavBridge — Imperative bridge for navigating Stackflow from outside its tree.
 *
 * DesktopContactList sits outside the Stackflow provider (sibling of MessagingStack),
 * so it cannot use useMessagingFlow(). This bridge stores references to push/replace
 * that are registered from components inside the Stackflow tree (MessagesPage, ChatPage).
 *
 * **Pending deep-link**: Any code outside the messaging tree can call
 * `setPendingChat(conversationId)` then navigate to `/messages`. When
 * MessagesPage mounts and registers the flow, it flushes the pending ID
 * by pushing ChatActivity automatically.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyFn = (...args: any[]) => any;

interface MessagingFlowActions {
  push: AnyFn;
  replace: AnyFn;
}

let flow: MessagingFlowActions | null = null;
let pendingConversationId: string | null = null;
const listeners = new Set<() => void>();

// ── General pending push (for mobile → Stackflow handoff) ──────────

interface PendingPush {
  activity: string;
  params: Record<string, string>;
}

let pendingPush: PendingPush | null = null;

/** Queue any Stackflow activity to push when the messaging stack mounts. */
export function setPendingPush(activity: string, params: Record<string, string> = {}) {
  pendingPush = { activity, params };
}

/** Consume (and clear) the pending push. */
export function consumePendingPush(): PendingPush | null {
  const p = pendingPush;
  pendingPush = null;
  return p;
}

/** Read without consuming. */
export function hasPendingPush(): boolean {
  return pendingPush !== null || pendingConversationId !== null;
}

export function registerMessagingFlow(actions: MessagingFlowActions) {
  flow = actions;
  // Flush general pending push first (new API)
  const push = consumePendingPush();
  if (push) {
    flow.push(push.activity, push.params);
    notifyListeners();
    return;
  }
  // Legacy: flush pending chat
  const pending = consumePendingChat();
  if (pending) {
    flow.push('ChatActivity', { conversationId: pending });
    notifyListeners();
  }
}

export function unregisterMessagingFlow() {
  flow = null;
}

export function getMessagingFlow(): MessagingFlowActions | null {
  return flow;
}

// ── Pending deep-link API ───────────────────────────────────────────

/** Set a conversation to open when the messaging stack mounts. */
export function setPendingChat(conversationId: string) {
  pendingConversationId = conversationId;
}

/** Consume (and clear) the pending conversation ID. */
export function consumePendingChat(): string | null {
  const id = pendingConversationId;
  pendingConversationId = null;
  return id;
}

/** Read without consuming (for highlight sync). */
export function peekPendingChat(): string | null {
  return pendingConversationId;
}

// ── Listener for DesktopContactList to sync highlight ────────────────

export function onPendingChatFlushed(cb: () => void) {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

function notifyListeners() {
  listeners.forEach((cb) => cb());
}
